/**
 * Filterable Cards - filtering + entrance animation frontend.
 *
 * Build entry: src/js/modules/filterable-cards/frontend.js
 * Output:      assets/js/modules/filterable-cards-frontend.min.js
 *
 * Self-contained IIFE - no imports, no build-time shared chunks.
 */
(() => {
	/** Respect the OS-level reduced-motion setting. */
	function prefersReducedMotion() {
		return (
			typeof window.matchMedia === "function" &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches
		);
	}

	// Entrance animation (UiCore Animate protocol): cards carry
	// data-ui-animation + data-ui-duration + --ui-anim-delay and start with
	// the `uicore-animate-hide` class. On intersection we add `animated`
	// (+ speed class) and the animation-name classes, then reveal exactly
	// when the CSS animation starts.
	var observer = null;

	function getObserver() {
		if (observer) {
			return observer;
		}
		observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) {
						return;
					}
					var el = entry.target;

					var speed = el.getAttribute("data-ui-duration");
					if (speed === "fast") {
						el.classList.add("animated", "animated-fast");
					} else if (speed === "slow") {
						el.classList.add("animated", "animated-slow");
					} else {
						el.classList.add("animated");
					}

					var animName = el.getAttribute("data-ui-animation");
					if (animName) {
						el.classList.add.apply(el.classList, animName.split(" "));
					}

					var onStart = () => {
						el.classList.remove("uicore-animate-hide");
						el.removeEventListener("animationstart", onStart);
					};
					el.addEventListener("animationstart", onStart, { once: true });

					requestAnimationFrame(() => {
						if (getComputedStyle(el).animationDelay === "0s") {
							el.classList.remove("uicore-animate-hide");
						}
					});

					observer.unobserve(el);
				});
			},
			{ rootMargin: "10px", threshold: 0 },
		);
		return observer;
	}

	/**
	 * Strip all entrance-animation state (hide class + animated classes) so
	 * a card can be revealed without waiting for its per-card stagger delay.
	 *
	 * @param {Element} el Card element.
	 */
	function clearEntranceState(el) {
		el.classList.remove(
			"uicore-animate-hide",
			"animated",
			"animated-fast",
			"animated-slow",
		);
		var animName = el.getAttribute("data-ui-animation");
		if (animName) {
			el.classList.remove.apply(el.classList, animName.split(" "));
		}
	}

	// ------------------------------------------------------------------
	// Filtering
	// ------------------------------------------------------------------

	/**
	 * Wire the filter behaviour for one widget root.
	 *
	 * @param {Element} root Widget wrapper (.mypro-filterable-cards).
	 */
	function initFiltering(root) {
		var bar = root.querySelector(".services-filter");
		var grid = root.querySelector(".services-grid");
		if (!bar || !grid) {
			return; // No filter bar -> nothing to filter.
		}

		var buttons = Array.prototype.slice.call(
			bar.querySelectorAll(".filter-btn"),
		);
		var empty = grid.querySelector(".filter-empty");
		var animateOnFilter = root.getAttribute("data-mypro-fc-animated") !== "no";
		var currentFilter = "all";

		function cardMatches(card, filter) {
			if (filter === "all") {
				return true;
			}
			var cats = (card.getAttribute("data-category") || "").split(/\s+/);
			return cats.indexOf(filter) !== -1;
		}

		function applyFilter(filter, isInitial) {
			// Re-clicking the active filter must not re-animate the grid.
			if (!isInitial && filter === currentFilter) {
				return;
			}
			currentFilter = filter;

			var anyVisible = false;
			buttons.forEach((btn) => {
				var active = btn.getAttribute("data-filter") === filter;
				btn.classList.toggle("active", active);
				btn.setAttribute("aria-pressed", active ? "true" : "false");
			});

			var cards = grid.querySelectorAll(".service-card");
			var visibleTotal = cards.length;
			for (var i = 0; i < cards.length; i++) {
				var card = cards[i];
				var show = cardMatches(card, filter);
				anyVisible = anyVisible || show;

				if (show) {
					card.classList.remove("service-card-hidden");
					if (!isInitial) {
						// User switched filters: reveal promptly. The entrance
						// animation's stagger delay is indexed to the "all"
						// grid position, so a lone card (e.g. hybrid, the 5th
						// card) would wait 600ms+ before appearing. Use a short
						// unstaggered fade instead, applied to every card at
						// once.
						clearEntranceState(card);
						if (animateOnFilter && !prefersReducedMotion()) {
							card.classList.remove("mypro-fc-filter-in");
							void card.offsetWidth; // force reflow so the reveal restarts
							card.classList.add("mypro-fc-filter-in");
						}
					} else if (
						card.hasAttribute("data-ui-animation") &&
						!prefersReducedMotion()
					) {
						// Initial load: the entrance observer staggers cards in
						// as they scroll into view.
						getObserver().observe(card);
					} else {
						card.classList.remove("uicore-animate-hide");
					}
				} else {
					card.classList.add("service-card-hidden");
					card.classList.remove("mypro-fc-filter-in");
				}
			}

			grid.classList.toggle("is-empty", !anyVisible && visibleTotal > 0);
			if (empty) {
				empty.hidden = anyVisible || visibleTotal === 0;
			}
		}

		buttons.forEach((btn) => {
			btn.addEventListener("click", () => {
				applyFilter(btn.getAttribute("data-filter"));
			});
		});

		// "all" is active by default; reflect it immediately.
		applyFilter("all", true);
	}

	// ------------------------------------------------------------------
	// Boot / wiring
	// ------------------------------------------------------------------

	/** Wire one widget root. */
	function initRoot(root) {
		if (
			!root ||
			!root.querySelector ||
			root.getAttribute("data-mypro-fc-ready")
		) {
			return;
		}
		root.setAttribute("data-mypro-fc-ready", "1");

		var cards = root.querySelectorAll(".service-card");

		// Reduced motion: reveal everything immediately, never animate.
		if (prefersReducedMotion()) {
			for (var r = 0; r < cards.length; r++) {
				cards[r].classList.remove("uicore-animate-hide");
			}
		} else {
			for (var j = 0; j < cards.length; j++) {
				if (cards[j].hasAttribute("data-ui-animation")) {
					getObserver().observe(cards[j]);
				}
			}
		}

		initFiltering(root);
	}

	/** Sweep the document for every widget root. */
	function initAll() {
		var roots = document.querySelectorAll(".mypro-filterable-cards");
		for (var i = 0; i < roots.length; i++) {
			initRoot(roots[i]);
		}
	}

	/**
	 * Register the Elementor `element_ready` hook. Runs only after
	 * `elementorFrontend.init()` (the "elementor/frontend/init" window
	 * event), because the public page boots before this script and the editor
	 * boots the iframe instance later. Both the suffixed and bare widget type
	 * names are registered.
	 */
	function registerElementReadyHook() {
		if (
			!window.elementorFrontend ||
			!window.elementorFrontend.hooks ||
			window.elementorFrontend.hooks.__myproFilterableCardsReady
		) {
			return;
		}
		window.elementorFrontend.hooks.__myproFilterableCardsReady = true;

		var onReady = ($scope) => {
			initRoot($scope && $scope[0] ? $scope[0] : $scope);
		};
		window.elementorFrontend.hooks.addAction(
			"frontend/element_ready/mypro_filterable_cards.default",
			onReady,
		);
		window.elementorFrontend.hooks.addAction(
			"frontend/element_ready/mypro_filterable_cards",
			onReady,
		);
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initAll);
	} else {
		initAll();
	}

	if (window.elementorFrontend && window.elementorFrontend.hooks) {
		registerElementReadyHook();
	}
	window.addEventListener("elementor/frontend/init", registerElementReadyHook);

	// Version-proof fallback: rAF-throttled sweep for widget nodes that appear
	// after this script ran (editor drag-in, control re-render, lazy panels).
	var sweepQueued = false;
	function queueSweep() {
		if (sweepQueued) {
			return;
		}
		sweepQueued = true;
		requestAnimationFrame(() => {
			sweepQueued = false;
			initAll();
		});
	}
	if (typeof MutationObserver !== "undefined") {
		new MutationObserver(() => {
			queueSweep();
		}).observe(document.documentElement, {
			childList: true,
			subtree: true,
		});
	}
})();
